"use client";

import { useEffect, useState, type AnimationEvent, type CSSProperties } from "react";
import type { WPrezentationCardsContent, WPrezentationSlide } from "@/lib/api/cms-window-content";
import { useT } from "@/lib/i18n/context";
import { wPrezentationCardsTranslations, type WPrezentationCardsTranslations } from "./WPrezentationCards.i18n";
import styles from "./WPrezentationCards.module.css";

/**
 * WPrezentationCards — wariant "karty" prezentacji (typ okna `w_prezentation_cards`).
 *
 * Wydzielony z `WPrezentation` — obsluguje wylacznie stackowy layout (fanned cards
 * + tabs progress bar + bottom link). Dane slajdow identyczne jak w `WPrezentation`
 * (image / video / youtube), inny rendering.
 *
 * **Stack cards bez Swipera** — wlasny CardsCarousel z transform per slajd, loop
 * przez modulo, autoplay przez setInterval. Unikamy bugow EffectCards + loop w
 * Swiper 12.
 */
export default function WPrezentationCards({ content }: WPrezentationCardsProps) {
  const t = useT(wPrezentationCardsTranslations);

  if (!content || content.slides.length === 0) {
    return (
      <div className={styles.empty} role="status">
        {t.empty}
      </div>
    );
  }

  const { config, slides } = content;
  const autoplayEnabled = config.slideshow && slides.length > 1;

  const [activeIndex, setActiveIndex] = useState(0);

  /* Cards mode autoplay: wlasny setInterval, activeIndex loopuje przez
     modulo. Autoplay leci ciagle, nawet gdy kursor jest nad slajdem. */
  useEffect(() => {
    if (!autoplayEnabled) return;
    const id = setInterval(() => {
      setActiveIndex((i) => (i + 1) % slides.length);
    }, config.slideshow_freq * 1000);
    return () => clearInterval(id);
  }, [autoplayEnabled, slides.length, config.slideshow_freq]);

  return (
    <div
      className={styles.root}
      aria-roledescription="carousel"
      aria-label={content.title}
      style={
        autoplayEnabled
          ? ({ "--slide-duration": `${config.slideshow_freq}s` } as CSSProperties)
          : undefined
      }
    >
      <div className={styles.cardsStage}>
        <CardsCarousel
          slides={slides}
          showTitle={config.show_title}
          transitionSpeed={config.slideshow_speed}
          activeIndex={activeIndex}
          onSlideClick={(i) => setActiveIndex(i)}
          t={t}
        />
      </div>

      {config.show_tabs && slides.length > 1 && (
        <div
          className={styles.tabs}
          role="tablist"
          aria-label={content.title}
        >
          {slides.map((slide, index) => {
            const label = getTabLabel(slide, index);
            const isActive = index === activeIndex;
            return (
              <button
                key={slide.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`${styles.tab} ${isActive ? styles.tabActive : ""}`}
                onClick={() => setActiveIndex(index)}
              >
                <span className={styles.tabTrack} aria-hidden="true">
                  {isActive && autoplayEnabled && (
                    <span
                      key={activeIndex}
                      className={styles.tabFill}
                      style={{ animationDuration: `${config.slideshow_freq}s` } as CSSProperties}
                    />
                  )}
                </span>
                {label}
              </button>
            );
          })}
        </div>
      )}

      {config.bottom_link_url && config.bottom_link_label && (
        <a href={config.bottom_link_url} className={styles.bottomLink}>
          <span>{config.bottom_link_label}</span>
          <ChevronIcon direction="right" />
        </a>
      )}
    </div>
  );
}

function getTabLabel(slide: WPrezentationSlide, index: number): string {
  if (!slide.name) return `${index + 1}`;
  const stripped = slide.name.replace(/<[^>]+>/g, "").trim();
  return stripped || `${index + 1}`;
}

function ChevronIcon({ direction }: { direction: "left" | "right" }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {direction === "left" ? (
        <polyline points="15 18 9 12 15 6" />
      ) : (
        <polyline points="9 18 15 12 9 6" />
      )}
    </svg>
  );
}

export type WPrezentationCardsProps = {
  content: WPrezentationCardsContent | null;
};

/* ─── CardsCarousel - wlasny stack cards bez Swipera ────────────────────
   Kazdy slajd ma pozycje w stosunku do aktywnego (0 = aktywny na wierzchu,
   1 = pierwszy za, 2 = drugi za). Loop przez modulo, autoplay przez
   setInterval w rodzicu.

   Zmiana aktywnego slajdu: karta schodzaca z wierzchu dostaje klase
   `cardsSlideLeaving*` - jednorazowa animacja "wyrzucenia" na bok (kierunek
   naprzemiennie lewo/prawo) i powrotu na koniec stosu. Klasa znika po
   `animationend`, docelowa pozycje trzyma zwykla klasa `cardsSlidePos*`. */
function CardsCarousel({
  slides,
  showTitle,
  transitionSpeed,
  activeIndex,
  onSlideClick,
  t,
}: {
  slides: WPrezentationSlide[];
  showTitle: boolean;
  transitionSpeed: number;
  activeIndex: number;
  onSlideClick: (index: number) => void;
  t: WPrezentationCardsTranslations;
}) {
  const [prevActive, setPrevActive] = useState(activeIndex);
  const [leaving, setLeaving] = useState<{ index: number; dir: "left" | "right" } | null>(null);
  const [throwLeft, setThrowLeft] = useState(true);

  /* Zmiana aktywnego wykrywana w trakcie renderu (wzorzec "state z poprzedniego
     renderu"), a nie w useEffect - klasa animacji musi wejsc w TYM SAMYM
     commicie co nowa pozycja, inaczej przez klatke rusza zwykla tranzycja. */
  if (prevActive !== activeIndex) {
    setPrevActive(activeIndex);
    setThrowLeft(!throwLeft);
    setLeaving({ index: prevActive, dir: throwLeft ? "left" : "right" });
  }

  const handleAnimationEnd = (index: number) => (e: AnimationEvent<HTMLDivElement>) => {
    /* Ignorujemy animacje dzieci (kenBurns na obrazku) - tylko wlasna karty. */
    if (e.target !== e.currentTarget) return;
    setLeaving((cur) => (cur && cur.index === index ? null : cur));
  };

  return (
    <div
      className={styles.cardsCarouselStage}
      style={{ "--slide-speed": `${transitionSpeed}ms` } as CSSProperties}
    >
      {slides.map((slide, index) => {
        const position = (index - activeIndex + slides.length) % slides.length;
        const posClass =
          position === 0
            ? styles.cardsSlidePos0
            : position === 1
            ? styles.cardsSlidePos1
            : position === 2
            ? styles.cardsSlidePos2
            : styles.cardsSlideHidden;
        const isActive = position === 0;
        const leavingClass =
          leaving && leaving.index === index && !isActive
            ? leaving.dir === "left"
              ? styles.cardsSlideLeavingLeft
              : styles.cardsSlideLeavingRight
            : "";
        return (
          <div
            key={slide.id}
            className={`${styles.cardsSlideItem} ${posClass} ${leavingClass}`}
            onAnimationEnd={handleAnimationEnd(index)}
            aria-hidden={!isActive}
            onClick={() => {
              if (!isActive) onSlideClick(index);
            }}
          >
            <SlideContent
              slide={slide}
              showOverlay={showTitle}
              priority={index === 0}
              t={t}
            />
          </div>
        );
      })}
    </div>
  );
}

/* ─── Slide renderer ──────────────────────────────────────────────── */

function SlideContent({
  slide,
  showOverlay,
  priority,
  t,
}: {
  slide: WPrezentationSlide;
  showOverlay: boolean;
  priority: boolean;
  t: WPrezentationCardsTranslations;
}) {
  const bgStyle = slide.background_color
    ? { backgroundColor: `#${slide.background_color}` }
    : undefined;

  const hasOverlayContent = Boolean(slide.name || slide.subname || slide.href);
  const renderOverlay = showOverlay && hasOverlayContent;

  return (
    <div className={styles.slide} style={bgStyle}>
      <div className={styles.media}>
        <SlideMedia slide={slide} priority={priority} t={t} />
      </div>

      {slide.href && (
        <a
          href={slide.href}
          className={styles.slideLink}
          aria-label={slide.name ?? t.openSlideAria}
        />
      )}

      {renderOverlay && (
        <div className={styles.overlay}>
          {slide.name && (
            <h2
              className={styles.title}
              dangerouslySetInnerHTML={{ __html: slide.name }}
            />
          )}
          {slide.subname && (() => {
            /* Textarea w adminie -> kazda nowa linia (\n lub <br>) to osobny
               segment. Renderujemy jako spany oddzielone kropka `·` z CSS
               ::before (patrz .subtitle > span:not(:first-child) w module.css).
               Zachowujemy dangerouslySetInnerHTML per segment - filtr admina
               pozwala na <h1>-<h3>/<p>/<span>/<br>, wiec inline HTML ma dzialac. */
            const items = slide.subname
              .split(/\r?\n|<br\s*\/?>/i)
              .map((s) => s.trim())
              .filter((s) => s.length > 0);
            if (items.length === 0) return null;
            return (
              <p className={styles.subtitle}>
                {items.map((item, i) => (
                  <span
                    key={i}
                    className={styles.subtitleItem}
                    dangerouslySetInnerHTML={{ __html: item }}
                  />
                ))}
              </p>
            );
          })()}
          {slide.href && (
            <a href={slide.href} className={styles.cta}>
              {t.cta}
            </a>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Media renderer per slide.type ───────────────────────────────── */

function SlideMedia({ slide, priority, t }: { slide: WPrezentationSlide; priority: boolean; t: WPrezentationCardsTranslations }) {
  if (slide.type === 1 && slide.image) {
    const { desktop, tablet, mobile } = slide.image;
    if (!desktop) return null;

    return (
      <picture>
        {mobile && <source media="(max-width: 767px)" srcSet={mobile} />}
        {tablet && <source media="(max-width: 1024px)" srcSet={tablet} />}
        <img
          src={desktop}
          alt={slide.name ?? ""}
          loading={priority ? "eager" : "lazy"}
          fetchPriority={priority ? "high" : "auto"}
          decoding="async"
        />
      </picture>
    );
  }

  if (slide.type === 2 && slide.video) {
    return (
      <video autoPlay loop muted playsInline preload={priority ? "auto" : "metadata"}>
        <source src={slide.video.file_url} />
      </video>
    );
  }

  if (slide.type === 3 && slide.youtube) {
    const ytId = slide.youtube.video_id;
    const src = `https://www.youtube.com/embed/${ytId}?rel=0&modestbranding=1&controls=0&showinfo=0&autoplay=0&loop=1&mute=1&playlist=${ytId}`;
    return (
      <iframe
        src={src}
        title={slide.name ?? t.youtubeTitleFallback}
        loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    );
  }

  return null;
}
